import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
  InputNumber,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';

const formatHora = (h) => {
  if (!h) return '-';
  const s = String(h);
  if (s.length >= 5) return s.substring(0, 5);
  return s;
};

const TurnosCaja = () => {
  const { hasPermission } = usePermissions();
  const [turnos, setTurnos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTurno, setEditingTurno] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  const loadTurnos = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/gestion/turnos-caja?activos=false');
      if (response.ok) {
        const data = await response.json();
        setTurnos(data);
      } else {
        message.error('Error al cargar los turnos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los turnos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTurnos();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOpenModal = (turno = null) => {
    setEditingTurno(turno);
    if (turno) {
      form.setFieldsValue({
        nombre: turno.tca_nombre,
        hora_inicio: formatHora(turno.tca_hora_inicio),
        hora_fin: formatHora(turno.tca_hora_fin),
        cruza_medianoche: turno.tca_cruza_medianoche,
        activo: turno.tca_activo,
        orden: turno.tca_orden,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ activo: true, cruza_medianoche: false, orden: 0 });
    }
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        nombre: values.nombre.trim(),
        hora_inicio: values.hora_inicio,
        hora_fin: values.hora_fin,
        cruza_medianoche: !!values.cruza_medianoche,
        activo: values.activo !== false,
        orden: values.orden ?? 0,
      };
      if (editingTurno) {
        const response = await apiClient.put(`/gestion/turnos-caja/${editingTurno.tca_id}`, payload);
        if (response.ok) {
          message.success('Turno actualizado correctamente');
          setModalVisible(false);
          loadTurnos();
        } else {
          const err = await response.json();
          message.error(err.message || 'Error al actualizar');
        }
      } else {
        const response = await apiClient.post('/gestion/turnos-caja', payload);
        if (response.ok) {
          message.success('Turno creado correctamente');
          setModalVisible(false);
          loadTurnos();
        } else {
          const err = await response.json();
          message.error(err.message || 'Error al crear');
        }
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error('Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/gestion/turnos-caja/${id}`);
      if (response.ok) {
        message.success('Turno eliminado');
        loadTurnos();
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al eliminar');
      }
    } catch (error) {
      message.error('Error al eliminar');
    }
  };

  const columns = [
    { title: 'Orden', dataIndex: 'tca_orden', key: 'orden', width: 80, align: 'center' },
    { title: 'Nombre', dataIndex: 'tca_nombre', key: 'tca_nombre' },
    {
      title: 'Hora inicio',
      dataIndex: 'tca_hora_inicio',
      key: 'tca_hora_inicio',
      width: 120,
      render: (h) => formatHora(h),
    },
    {
      title: 'Hora fin',
      dataIndex: 'tca_hora_fin',
      key: 'tca_hora_fin',
      width: 120,
      render: (h) => formatHora(h),
    },
    {
      title: 'Cruza medianoche',
      dataIndex: 'tca_cruza_medianoche',
      key: 'tca_cruza_medianoche',
      width: 140,
      align: 'center',
      render: (v) => <Tag color={v ? 'orange' : 'default'}>{v ? 'Sí' : 'No'}</Tag>,
    },
    {
      title: 'Activo',
      dataIndex: 'tca_activo',
      key: 'tca_activo',
      width: 90,
      align: 'center',
      render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? 'Sí' : 'No'}</Tag>,
    },
    ...((hasPermission('turnos_caja', 'editar') || hasPermission('turnos_caja', 'eliminar')) ? [{
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space>
          {hasPermission('turnos_caja', 'editar') && (
            <Button type="link" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} size="small">
              Editar
            </Button>
          )}
          {hasPermission('turnos_caja', 'eliminar') && (
            <Popconfirm
              title="¿Eliminar este turno?"
              onConfirm={() => handleDelete(record.tca_id)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} size="small">
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <h1 style={{ marginBottom: 16, color: '#fff' }}>Turnos de atención</h1>
        <p style={{ color: '#b0b0b0', marginBottom: 16 }}>
          Definí los horarios de cada turno (ej. Mañana 6–17, Noche 17–2). Si el turno pasa las 12, marcá "Cruza medianoche".
        </p>
        <Space style={{ marginBottom: 16 }}>
          {hasPermission('turnos_caja', 'crear') && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal(null)}>
              Nuevo turno
            </Button>
          )}
          <Button icon={<ReloadOutlined />} onClick={loadTurnos} loading={loading}>
            Actualizar
          </Button>
        </Space>
        <Card>
          <Table
            columns={columns}
            dataSource={turnos}
            rowKey="tca_id"
            loading={loading}
            pagination={false}
            size="small"
          />
        </Card>

        <Modal
          title={editingTurno ? 'Editar turno' : 'Nuevo turno'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => setModalVisible(false)}
          okText="Guardar"
          cancelText="Cancelar"
          width={400}
        >
          <Form form={form} layout="vertical">
            <Form.Item name="nombre" label="Nombre" rules={[{ required: true }]}>
              <Input placeholder="Ej. Mañana, Noche" />
            </Form.Item>
            <Form.Item name="hora_inicio" label="Hora inicio" rules={[{ required: true }]}>
              <Input type="time" />
            </Form.Item>
            <Form.Item name="hora_fin" label="Hora fin" rules={[{ required: true }]}>
              <Input type="time" />
            </Form.Item>
            <Form.Item name="cruza_medianoche" valuePropName="checked" label="Cruza medianoche">
              <Switch />
            </Form.Item>
            <Form.Item name="orden" label="Orden">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="activo" valuePropName="checked" label="Activo">
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default TurnosCaja;
